import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import gsap from 'gsap'
import { PackState } from '../../types/pack-animation'
import { Card } from '../../types'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PackOpening3DProps {
  packTexture: string
  cardTexture?: string
  onComplete?: () => void
  onCardReveal?: () => void
  revealedCards: Card[]
}

const vertexShader = `
  uniform float uTime;
  uniform float uRipProgress;
  uniform float uRipLineY;
  uniform vec2 uMouse;
  uniform float uHover;
  
  varying vec2 vUv;
  varying float vDeformation;
  varying float vRipDist;
  
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
  
  void main() {
    vUv = uv;
    vDeformation = 0.0;
    
    vec3 pos = position;
    
    // Línea de rasgado IRREGULAR usando noise
    float ripNoise = snoise(vec3(uv.x * 5.0, 0.0, 0.0));
    float ripY = uRipLineY + ripNoise * 0.05;
    
    vRipDist = abs(uv.x - uRipProgress);
    
    if (uv.y < ripY && uRipProgress > 0.0) {
      float tension = smoothstep(0.15, 0.0, vRipDist);
      
      float tearZ = snoise(vec3(uv.x * 10.0 + uTime, uTime * 2.0, 0.0)) * 0.12 * tension;
      float tearY = snoise(vec3(uv.x * 8.0 + 100.0, uTime * 1.5, 100.0)) * 0.08 * tension;
      float tearX = snoise(vec3(uv.x * 12.0 + 200.0, uTime * 2.5, 200.0)) * 0.06 * tension;
      
      pos.z += tearZ;
      pos.y += tearY;
      pos.x += tearX;
      
      vDeformation = tension;
    }
    
    // Parallax por hover
    if (uHover > 0.5) {
      pos.x += uMouse.x * 0.05;
      pos.y += uMouse.y * 0.05;
    }
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`

const fragmentShader = `
  uniform sampler2D uFrontTexture;
  uniform sampler2D uBackTexture;
  uniform float uRipProgress;
  uniform float uRipLineY;
  uniform float uTime;
  
  varying vec2 vUv;
  varying float vDeformation;
  varying float vRipDist;
  
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
  
  void main() {
    float ripNoise = snoise(vec3(vUv.x * 5.0, 0.0, 0.0));
    float ripY = uRipLineY + ripNoise * 0.05;
    
    if (gl_FrontFacing && uRipProgress > 0.8 && vUv.y < ripY && vUv.x > uRipProgress) {
      discard;
    }
    
    if (!gl_FrontFacing) {
      vec4 backColor = texture2D(uBackTexture, vUv);
      gl_FragColor = backColor * 0.6;
    } else {
      vec4 frontColor = texture2D(uFrontTexture, vUv);
      
      if (vDeformation > 0.0) {
        vec3 gold = vec3(1.0, 0.84, 0.0);
        frontColor.rgb += gold * vDeformation * 0.4;
      }
      
      gl_FragColor = frontColor;
    }
  }
`

export function PackOpening3D({ packTexture, cardTexture, onComplete, onCardReveal, revealedCards }: PackOpening3DProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const threeContainerRef = useRef<HTMLDivElement>(null)
  const packWrapperRef = useRef<HTMLDivElement>(null)
  const cardsContainerRef = useRef<HTMLDivElement>(null)
  const cardElementsRef = useRef<HTMLDivElement[]>([])
  const thumbnailElementsRef = useRef<HTMLDivElement[]>([])
  
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const meshRef = useRef<THREE.Mesh | null>(null)
  const materialRef = useRef<THREE.ShaderMaterial | null>(null)
  const animationFrameRef = useRef<number | undefined>(undefined)
  
  const [currentState, setCurrentState] = useState<PackState>(PackState.IDLE)
  const [isHovering, setIsHovering] = useState(false)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [draggingCard, setDraggingCard] = useState(false)
  const [cardDragX, setCardDragX] = useState(0)
  const [cardRotation, setCardRotation] = useState(0)
  const [hasInitializedCards, setHasInitializedCards] = useState(false)
  const [cardsToDisplay, setCardsToDisplay] = useState<Card[]>([])
  const [revealedThumbnailIndices, setRevealedThumbnailIndices] = useState<Set<number>>(new Set())
  const [cardFlipped, setCardFlipped] = useState(false)
  const [cardHovered, setCardHovered] = useState(false)
  
  const [ripProgress, setRipProgress] = useState(0)
  const [isRipping, setIsRipping] = useState(false)
  const [ripStart, setRipStart] = useState({ x: 0 })
  const [imageSize, setImageSize] = useState({ width: 400, height: 533 })
  const [packAspectRatio, setPackAspectRatio] = useState(400 / 533)

  useEffect(() => {
    if (revealedCards.length > 0 && !hasInitializedCards) {
      const limited = revealedCards.slice(0, 10)
      setCardsToDisplay(limited)
      setHasInitializedCards(true)
    }
  }, [revealedCards, hasInitializedCards])

  const createInteriorTexture = (): THREE.CanvasTexture => {
    const canvas = document.createElement('canvas')
    canvas.width = 512
    canvas.height = 512
    const ctx = canvas.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(canvas)
    
    ctx.fillStyle = '#3d2b1f'
    ctx.fillRect(0, 0, 512, 512)
    
    ctx.strokeStyle = '#2a1f14'
    ctx.lineWidth = 2
    for (let i = 0; i < 512; i += 32) {
      ctx.beginPath()
      ctx.moveTo(0, i)
      ctx.lineTo(512, i)
      ctx.stroke()
    }
    
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    return texture
  }

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const aspectRatio = img.width / img.height
      setImageSize({ width: img.width, height: img.height })
      setPackAspectRatio(aspectRatio)
    }
    img.src = packTexture
  }, [packTexture])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!threeContainerRef.current || imageSize.width === 0) return
      
      const container = threeContainerRef.current
      const containerWidth = container.clientWidth || 400
      
      // Calcular altura basada en el aspect ratio real de la imagen
      const containerHeight = containerWidth / packAspectRatio
      
      const scene = new THREE.Scene()
      sceneRef.current = scene
      
      const camera = new THREE.PerspectiveCamera(50, containerWidth / containerHeight, 0.1, 100)
      camera.position.z = 2
      cameraRef.current = camera
      
      const renderer = new THREE.WebGLRenderer({ 
        antialias: true, 
        alpha: true,
        powerPreference: 'high-performance'
      })
      renderer.setSize(containerWidth, containerHeight)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      rendererRef.current = renderer
      
      renderer.domElement.style.width = '100%'
      renderer.domElement.style.height = '100%'
      
      container.innerHTML = ''
      container.appendChild(renderer.domElement)
      
      const interiorTexture = createInteriorTexture()
      const frontTextureLoader = new THREE.TextureLoader()
      
      frontTextureLoader.load(packTexture, (frontTexture) => {
        frontTexture.colorSpace = THREE.SRGBColorSpace
        
        // Geometry con el aspect ratio exacto de la imagen
        const geometry = new THREE.PlaneGeometry(packAspectRatio, 1.0, 40, 40)
        
        const material = new THREE.ShaderMaterial({
          uniforms: {
            uTime: { value: 0 },
            uRipProgress: { value: 0 },
            uRipLineY: { value: 0.35 },
            uMouse: { value: new THREE.Vector2(0, 0) },
            uHover: { value: 0 },
            uFrontTexture: { value: frontTexture },
            uBackTexture: { value: interiorTexture }
          },
          vertexShader,
          fragmentShader,
          transparent: true,
          side: THREE.DoubleSide
        })
        materialRef.current = material
        
        const mesh = new THREE.Mesh(geometry, material)
        scene.add(mesh)
        meshRef.current = mesh
        
        startRenderLoop()
      })
    }, 100)
    
    return () => {
      clearTimeout(timeout)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (rendererRef.current) {
        rendererRef.current.dispose()
      }
      if (materialRef.current) {
        materialRef.current.uniforms.uFrontTexture.value?.dispose()
        materialRef.current.uniforms.uBackTexture.value?.dispose()
      }
    }
  }, [packTexture, packAspectRatio])

  const startRenderLoop = () => {
    const render = () => {
      if (materialRef.current && (currentState === PackState.IDLE || currentState === PackState.HOVER || isRipping)) {
        const time = Date.now() * 0.001
        
        materialRef.current.uniforms.uTime.value = time
        materialRef.current.uniforms.uHover.value = isHovering ? 1.0 : 0.0
        materialRef.current.uniforms.uMouse.value.set(mousePos.x, mousePos.y)
        materialRef.current.uniforms.uRipProgress.value = ripProgress / 100
      }
      
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current)
      }
      
      animationFrameRef.current = requestAnimationFrame(render)
    }
    
    render()
  }

  const handleMouseEnter = () => {
    if (currentState === PackState.IDLE) {
      setIsHovering(true)
      setCurrentState(PackState.HOVER)
    }
  }

  const handleMouseLeave = () => {
    if (currentState === PackState.HOVER) {
      setIsHovering(false)
      setCurrentState(PackState.IDLE)
      setMousePos({ x: 0, y: 0 })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (currentState !== PackState.IDLE && currentState !== PackState.HOVER) return
    
    const rect = packWrapperRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    setMousePos({ x, y })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (currentState !== PackState.IDLE && currentState !== PackState.HOVER) return
    
    const rect = packWrapperRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const relativeY = e.clientY - rect.top
    const packHeight = rect.height
    
    if (relativeY < packHeight * 0.4) {
      setIsRipping(true)
      setRipProgress(0)
      setRipStart({ x: e.clientX })
    }
  }

  const handleMouseMoveRip = (e: React.MouseEvent) => {
    if (!isRipping || currentState !== PackState.IDLE && currentState !== PackState.HOVER) return
    
    const rect = packWrapperRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const dx = e.clientX - ripStart.x
    const maxRipDistance = rect.width * 0.8
    const progress = Math.max(0, Math.min(100, (dx / maxRipDistance) * 100))
    
    setRipProgress(progress)
    
    if (progress >= 100) {
      finishRip()
    }
  }

  const handleMouseUp = () => {
    if (isRipping && ripProgress < 100) {
      setRipProgress(0)
    }
    setIsRipping(false)
  }

  const finishRip = () => {
    setIsRipping(false)
    setCurrentState(PackState.RIP)
    animateFlash()
  }

  const animateFlash = () => {
    setCurrentState(PackState.FLASH)
    
    const flashOverlay = document.createElement('div')
    flashOverlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: white;
      opacity: 0;
      pointer-events: none;
      z-index: 100;
    `
    document.body.appendChild(flashOverlay)
    
    gsap.to(flashOverlay, {
      opacity: 1,
      duration: 0.1,
      ease: 'power2.in',
      onComplete: () => {
        gsap.to(flashOverlay, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: () => {
            flashOverlay.remove()
            animateReveal()
          }
        })
      }
    })
  }

  const animateReveal = () => {
    setCurrentState(PackState.REVEAL)
    
    if (onCardReveal) {
      onCardReveal()
    }
    
    if (packWrapperRef.current) {
      gsap.to(packWrapperRef.current, {
        y: -600,
        opacity: 0,
        scale: 0.3,
        rotate: 60,
        duration: 0.5,
        ease: 'power2.inOut'
      })
    }
    
    setTimeout(() => {
      setCurrentState(PackState.DONE)
    }, 500)
  }

  const handleCardMouseDown = (e: React.MouseEvent, index: number) => {
    if (currentState !== PackState.DONE || index !== currentCardIndex) return
    if (e.button !== 0) return
    
    e.preventDefault()
    e.stopPropagation()
    setDraggingCard(true)
    setCardDragX(0)
    setCardRotation(0)
  }

  const handleCardMouseMove = (e: MouseEvent) => {
    if (!draggingCard) return
    
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const centerX = rect.width / 2
    const dx = e.clientX - rect.left - centerX
    
    setCardDragX(dx)
    setCardRotation(dx / 12)
  }

  const handleCardMouseUp = () => {
    if (!draggingCard) return
    
    if (Math.abs(cardDragX) > 100) {
      swipeCard()
    } else {
      resetCardPosition()
    }
    
    setDraggingCard(false)
    setCardDragX(0)
    setCardRotation(0)
  }

  useEffect(() => {
    if (draggingCard) {
      window.addEventListener('mousemove', handleCardMouseMove)
      window.addEventListener('mouseup', handleCardMouseUp)
    }
    
    return () => {
      window.removeEventListener('mousemove', handleCardMouseMove)
      window.removeEventListener('mouseup', handleCardMouseUp)
    }
  }, [draggingCard, cardDragX])

  const swipeCard = () => {
    const currentCardEl = cardElementsRef.current[currentCardIndex]
    if (!currentCardEl) return
    
    const direction = cardDragX > 0 ? 1 : -1
    
    gsap.to(currentCardEl, {
      x: direction * 400,
      rotation: direction * 35,
      opacity: 0,
      duration: 0.4,
      ease: 'power2.inOut',
      onComplete: () => {
        setCardFlipped(false)
        setCardHovered(false)
        setRevealedThumbnailIndices(prev => new Set([...prev, currentCardIndex]))
        setCurrentCardIndex(prev => prev + 1)
      }
    })
  }

  const resetCardPosition = () => {
    const currentCardEl = cardElementsRef.current[currentCardIndex]
    if (!currentCardEl) return
    
    gsap.to(currentCardEl, {
      x: 0,
      rotation: 0,
      duration: 0.25,
      ease: 'power2.out'
    })
  }

  const handleCardMouseEnter = () => {
    setCardHovered(true)
  }

  const handleCardMouseLeave = () => {
    setCardHovered(false)
  }

  useEffect(() => {
    if (currentState === PackState.DONE) {
      if (cardHovered) {
        setCardFlipped(true)
      } else {
        setTimeout(() => {
          if (!cardHovered) {
            setCardFlipped(false)
          }
        }, 150)
      }
    }
  }, [cardHovered, currentState])

  const goToCard = (index: number) => {
    if (index === currentCardIndex || index < 0 || index >= cardsToDisplay.length) return
    
    const direction = index > currentCardIndex ? 1 : -1
    
    if (currentCardIndex < cardsToDisplay.length - 1) {
      const currentCardEl = cardElementsRef.current[currentCardIndex]
      if (currentCardEl) {
        gsap.to(currentCardEl, {
          x: direction * -400,
          rotation: direction * -20,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.inOut',
          onComplete: () => {
            setCardFlipped(false)
            setCardHovered(false)
            setRevealedThumbnailIndices(prev => new Set([...prev, currentCardIndex]))
          }
        })
      }
    }
    
    setCurrentCardIndex(index)
  }

  const goToNextCard = () => {
    goToCard(currentCardIndex + 1)
  }

  const goToPreviousCard = () => {
    goToCard(currentCardIndex - 1)
  }

  const resetAnimation = () => {
    if (!packWrapperRef.current) return
    
    gsap.killTweensOf('*')
    cardElementsRef.current.forEach(card => {
      if (card) {
        gsap.killTweensOf(card)
      }
    })
    thumbnailElementsRef.current.forEach(thumb => {
      if (thumb) {
        gsap.killTweensOf(thumb)
      }
    })
    
    packWrapperRef.current.style.transform = 'translateY(0) rotate(0deg) scale(1)'
    packWrapperRef.current.style.opacity = '1'
    
    setCurrentCardIndex(0)
    setCurrentState(PackState.IDLE)
    setIsHovering(false)
    setMousePos({ x: 0, y: 0 })
    setHasInitializedCards(false)
    setCardsToDisplay([])
    setRevealedThumbnailIndices(new Set())
    setCardFlipped(false)
    setCardHovered(false)
    setRipProgress(0)
    setIsRipping(false)
    setRipStart({ x: 0 })
  }

  const setCardRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      cardElementsRef.current[index] = el
    }
  }

  const setThumbnailRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      thumbnailElementsRef.current[index] = el
    }
  }

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-screen overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseMoveCapture={handleMouseMoveRip}
      onMouseUp={handleMouseUp}
      onMouseLeaveCapture={handleMouseUp}
      style={{ backgroundColor: '#0f1115' }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div 
          ref={packWrapperRef}
          className="relative cursor-pointer"
          style={{ 
            transition: 'transform 0.1s ease', 
            transformStyle: 'preserve-3d',
            width: 'min(90vw, 400px)',
            height: `${400 / packAspectRatio}px`
          }}
        >
          <div 
            ref={threeContainerRef}
            className="absolute inset-0 w-full h-full"
          />
          
          {(currentState === PackState.IDLE || currentState === PackState.HOVER) && !isRipping && (
            <div className="absolute top-[20%] inset-x-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full border-2 border-amber-500/50 flex items-center justify-center animate-pulse mx-auto mb-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-ping" />
                </div>
                <p className="text-white/50 text-xs font-medium">Arrastra para abrir</p>
              </div>
            </div>
          )}
          
          {isRipping && ripProgress > 0 && (
            <div className="absolute top-[8%] right-4 pointer-events-none">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 backdrop-blur-sm flex items-center justify-center border-2 border-amber-500/50">
                <span className="text-amber-400 text-xl font-bold">{Math.round(ripProgress)}%</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {currentState === PackState.DONE && cardsToDisplay.length > 0 && (
        <div 
          ref={cardsContainerRef}
          className="absolute inset-0 flex flex-col items-center justify-center"
        >
          <div className="flex items-center gap-8 mb-8">
            {currentCardIndex > 0 && (
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPreviousCard()
                }}
                className="w-16 h-16 rounded-full bg-amber-500/20 hover:bg-amber-500/40 backdrop-blur-sm border-2 border-amber-500/50 flex items-center justify-center transition-all hover:scale-110"
              >
                <ChevronLeft size={32} className="text-amber-500" />
              </button>
            )}
            
            <div 
              className="relative w-96 h-[600px] perspective-1000"
              onMouseEnter={handleCardMouseEnter}
              onMouseLeave={handleCardMouseLeave}
            >
              {cardsToDisplay.map((card, index) => {
                const isCurrent = index === currentCardIndex
                const isPrevious = index < currentCardIndex
                
                if (isPrevious) return null
                
                return (
                  <div
                    key={`card-${index}`}
                    ref={setCardRef(index)}
                    className="absolute inset-0 rounded-2xl overflow-hidden border-2 shadow-2xl cursor-pointer"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: `
                        translate(${isCurrent ? cardDragX : 0}px, 0px)
                        rotate(${isCurrent ? cardRotation : 0}deg)
                        rotateY(${cardFlipped ? 180 : 0}deg)
                      `,
                      opacity: isCurrent ? 1 : 0,
                      zIndex: cardsToDisplay.length - index,
                      borderColor: isCurrent ? '#f59e0b' : '#2d3540',
                      transition: isCurrent && draggingCard ? 'none' : 'all 0.2s ease',
                      pointerEvents: isCurrent ? 'auto' : 'none',
                      cursor: isCurrent ? 'grab' : 'default'
                    }}
                    onMouseDown={(e) => handleCardMouseDown(e, index)}
                  >
                    <div className="absolute inset-0 backface-hidden">
                      <img
                        src={card.imageUrl || card.localImage || '/images/cards/placeholder.png'}
                        alt={card.name}
                        className="w-full h-full object-cover"
                        draggable={false}
                        onError={(e) => {
                          e.currentTarget.src = '/images/cards/placeholder.png'
                        }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-6">
                        <p className="text-white text-2xl font-bold text-center">{card.name}</p>
                        {card.rarity && (
                          <p className={`text-lg text-center mt-2 ${
                            card.rarity === 'Legendaria' ? 'text-amber-400' :
                            card.rarity === 'Épica' ? 'text-purple-400' :
                            card.rarity === 'Rara' ? 'text-blue-400' :
                            'text-gray-400'
                          }`}>
                            {card.rarity}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div 
                      className="absolute inset-0 backface-hidden bg-gradient-to-br from-gray-700 to-gray-900"
                      style={{
                        transform: 'rotateY(180deg)'
                      }}
                    >
                      <div className="w-full h-full flex items-center justify-center">
                        <img
                          src={card.imageUrl || card.localImage || '/images/cards/placeholder.png'}
                          alt={card.name}
                          className="w-full h-full object-cover"
                          style={{
                            transform: 'scaleX(-1)',
                            filter: 'brightness(0.4) grayscale(0.8)'
                          }}
                          draggable={false}
                          onError={(e) => {
                            e.currentTarget.src = '/images/cards/placeholder.png'
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-600/30 to-gray-900/50" />
                      </div>
                    </div>
                    
                    {isCurrent && (
                      <div className="absolute top-6 left-6 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
                        {currentCardIndex + 1} / {cardsToDisplay.length}
                      </div>
                    )}
                    
                    {isCurrent && !cardFlipped && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 hover:opacity-100 transition-opacity bg-black/20">
                        <div className="text-center">
                          <p className="text-white text-2xl font-bold">← →</p>
                          <p className="text-white/80 text-sm">Desliza o usa las flechas</p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              
              {currentCardIndex >= cardsToDisplay.length && (
                <div className="absolute inset-0 flex items-center justify-center bg-amber-500/10 rounded-2xl border-2 border-amber-500/30">
                  <div className="text-center">
                    <p className="text-amber-400 text-3xl font-bold mb-3">¡Completado!</p>
                    <p className="text-white/60 text-lg">Has visto todas las {cardsToDisplay.length} cartas</p>
                  </div>
                </div>
              )}
            </div>
            
            {currentCardIndex < cardsToDisplay.length - 1 && (
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextCard()
                }}
                className="w-16 h-16 rounded-full bg-amber-500/20 hover:bg-amber-500/40 backdrop-blur-sm border-2 border-amber-500/50 flex items-center justify-center transition-all hover:scale-110"
              >
                <ChevronRight size={32} className="text-amber-500" />
              </button>
            )}
          </div>
          
          {cardsToDisplay.length > 0 && (
            <div className="flex items-center gap-3 px-8 py-4 bg-black/40 backdrop-blur-sm rounded-2xl border border-amber-500/20">
              {cardsToDisplay.map((card, index) => {
                const isCurrent = index === currentCardIndex
                const isRevealed = revealedThumbnailIndices.has(index)
                const isVisible = index <= currentCardIndex || isRevealed
                
                if (!isVisible) {
                  return (
                    <div
                      key={`thumb-${index}`}
                      className="w-16 h-24 rounded-lg overflow-hidden border-2 border-gray-600 bg-gradient-to-br from-gray-700 to-gray-900"
                    >
                      <div className="w-full h-full relative">
                        <img
                          src={card.imageUrl || card.localImage || '/images/cards/placeholder.png'}
                          alt={card.name}
                          className="w-full h-full object-cover"
                          style={{
                            transform: 'scaleX(-1)',
                            filter: 'brightness(0.4) grayscale(0.8)'
                          }}
                          draggable={false}
                          onError={(e) => {
                            e.currentTarget.src = '/images/cards/placeholder.png'
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-600/30 to-gray-900/50" />
                      </div>
                    </div>
                  )
                }
                
                return (
                  <div
                    key={`thumb-${index}`}
                    ref={setThumbnailRef(index)}
                    className={`w-16 h-24 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      isCurrent ? 'border-amber-500 scale-110 shadow-lg shadow-amber-500/30' :
                      'border-gray-600 opacity-50'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation()
                      goToCard(index)
                    }}
                  >
                    <div className="w-full h-full relative bg-gradient-to-br from-gray-700 to-gray-900">
                      <img
                        src={card.imageUrl || card.localImage || '/images/cards/placeholder.png'}
                        alt={card.name}
                        className="w-full h-full object-cover"
                        style={{
                          transform: 'scaleX(-1)',
                          filter: 'brightness(0.4) grayscale(0.8)'
                        }}
                        draggable={false}
                        onError={(e) => {
                          e.currentTarget.src = '/images/cards/placeholder.png'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-600/30 to-gray-900/50" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          
          <div className="absolute top-6 right-6">
            <button 
              onClick={(e) => {
                e.stopPropagation()
                resetAnimation()
              }}
              className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-black text-lg rounded-2xl transition-all hover:scale-105 shadow-xl shadow-amber-500/30"
            >
              REINICIAR
            </button>
          </div>
        </div>
      )}
      
      {currentState === PackState.DONE && cardsToDisplay.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-white/60">No hay cartas disponibles</p>
          </div>
        </div>
      )}
      
      {currentState === PackState.RIP && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-white/50 text-base font-bold tracking-wide animate-pulse">Abriendo...</div>
        </div>
      )}
    </div>
  )
}
